import { useState, useEffect } from 'react';
import { ArrowLeftRight, CircleAlert, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { impersonationUtils } from '@utils/impersonation';
import { AsyncCombobox } from '@/components/form/async-combobox';
import { Alert } from '@/components/lumen/alert';
import { Button } from '@/components/lumen/button';
import { useLanguage } from '@contexts/LanguageContext';
import { formatTemplate } from '@/lib/format-template';

const SWITCH_USER_ID = 'impersonation-switch-user';

export default function ImpersonationBanner() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonationInfo, setImpersonationInfo] = useState<any>(null);
  const [impersonatedUser, setImpersonatedUser] = useState<any>(null);
  const [showSwitchUser, setShowSwitchUser] = useState(false);
  const [canImpersonate, setCanImpersonate] = useState(false);

  const updateImpersonationState = () => {
    const impersonating = impersonationUtils.isImpersonating();
    setIsImpersonating(impersonating);
    setImpersonationInfo(impersonationUtils.getImpersonationInfo());
    setImpersonatedUser(impersonationUtils.getImpersonatedUserInfo());

    try {
      const authDataRaw = localStorage.getItem(impersonating ? 'originalAuthData' : 'authData');
      if (authDataRaw) {
        const authData = JSON.parse(authDataRaw);
        const email = authData.email?.toLowerCase() || '';

        if (impersonating) {
          const originalEmail = authData.email?.toLowerCase() || '';
          setCanImpersonate(impersonationUtils.canImpersonate(originalEmail));
        } else {
          setCanImpersonate(impersonationUtils.canImpersonate(email));
        }
      } else {
        setCanImpersonate(false);
      }
    } catch {
      setCanImpersonate(false);
    }
  };

  useEffect(() => {
    updateImpersonationState();

    const handleImpersonationChange = () => {
      updateImpersonationState();
    };

    window.addEventListener('impersonationChanged', handleImpersonationChange);

    return () => {
      window.removeEventListener('impersonationChanged', handleImpersonationChange);
    };
  }, []);

  const handleSwitchUser = async (userId: string, userObject: any) => {
    if (!userId || !userObject?.origin?.id) return;

    setLoading(true);
    try {
      await impersonationUtils.stopImpersonation();
      await impersonationUtils.startImpersonation(userObject.origin.id, 'Admin impersonation switch');

      toast.success('User Switch Successful', {
        description: `Successfully switched to impersonating ${userObject.origin.email}`,
      });

      window.location.reload();
    } catch (error: any) {
      toast.error('Error', { description: error.message || 'Failed to switch user' });
    } finally {
      setLoading(false);
    }
  };

  if (!isImpersonating || !impersonationInfo) {
    return null;
  }

  const handleStopImpersonation = async () => {
    setLoading(true);
    try {
      await impersonationUtils.stopImpersonation();

      toast.success('Impersonation Stopped', { description: 'Successfully stopped impersonation' });

      window.location.reload();
    } catch (error: any) {
      toast.error('Error', { description: error.message || 'Failed to stop impersonation' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Sticky over the page: the opaque layer keeps scrolled content from showing through the warn tint. */}
      <div className="sticky top-0 z-50 mb-4 rounded-card bg-raised shadow-overlay">
        <Alert variant="warn" className="border-warn/50 bg-warn/15">
          <CircleAlert aria-hidden />
          <div className="col-start-2 flex w-full flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <p className="font-semibold text-sm text-warn-ink">
                {formatTemplate(t('lumen.frame.impersonatingBanner'), {
                  target:
                    impersonatedUser?.email ||
                    formatTemplate(t('lumen.frame.impersonationUserId'), { id: impersonationInfo.targetUserId }),
                })}
              </p>
              {impersonatedUser?.name && <p className="text-fg-2 text-xs">({impersonatedUser.name})</p>}
              {impersonationInfo.reason && !impersonatedUser?.name && (
                <p className="text-fg-2 text-xs">({impersonationInfo.reason})</p>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2.5">
              {canImpersonate && (
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={() => setShowSwitchUser(!showSwitchUser)}
                  disabled={loading}
                >
                  <ArrowLeftRight aria-hidden />
                  {t('lumen.frame.switchUser')}
                </Button>
              )}

              <Button type="button" variant="outline" size="xs" onClick={handleStopImpersonation} disabled={loading}>
                {loading ? (
                  <Loader2 aria-hidden className="animate-spin motion-reduce:animate-none" />
                ) : (
                  <X aria-hidden />
                )}
                {loading ? t('lumen.frame.stopping') : t('lumen.frame.stopImpersonation')}
              </Button>
            </div>
          </div>
        </Alert>
      </div>

      {showSwitchUser && canImpersonate && (
        <div className="mx-4 mb-4 rounded-card border border-line bg-raised p-4">
          <label htmlFor={SWITCH_USER_ID} className="mb-3 block font-semibold text-fg text-sm">
            {t('lumen.frame.switchToAnotherUser')}
          </label>
          <AsyncCombobox
            id={SWITCH_USER_ID}
            placeholder={t('lumen.frame.searchUsers')}
            dynamicKeyParam="query"
            path="/api/v1/users"
            additionalParam="orderBy=email&ascending=true"
            onChange={handleSwitchUser}
            valueOnResponseKey="id"
            optionsLabelKeys={['email', 'name']}
            value=""
            disabled={loading}
            loadingText={t('lumen.pibEngine.lookup.loading')}
            emptyText={t('lumen.pibEngine.lookup.empty')}
            loadErrorText={formatTemplate(t('lumen.pibEngine.lookup.failed'), { label: '' })}
            aria-describedby={`${SWITCH_USER_ID}-hint`}
          />
          <p id={`${SWITCH_USER_ID}-hint`} className="mt-2.5 text-fg-muted text-xs">
            {t('lumen.frame.switchUserHint')}
          </p>
        </div>
      )}
    </>
  );
}
